import { useState, useEffect } from 'react';
import { formatMoney } from '../../utils/format';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Save, Loader2, AlertCircle, CheckCircle, Building2, Phone, Mail, Globe, MapPin, Palette, Image, Bell, CreditCard, Percent, DollarSign, Inbox, Clock } from 'lucide-react';
import { portalSettingsAPI, adminTicketsAPI, displaySettingsAPI } from '../../services/api';
import { DEFAULT_LOCALE, DEFAULT_CURRENCY } from '../../context/brandingStore';
import { useModuleEntitled } from '../../context/featureFlagStore';
import { NotInPlanNotice } from '../../components/common/NotInPlan';
import DisplayOverridesEditor from '../../components/admin/DisplayOverridesEditor';
import SettingsSidebar from '../../components/admin/SettingsSidebar';
import { REGISTRY_CATEGORIES } from '../../config/displayRegistry';
import AutopayChargingCard from '../../components/admin/AutopayChargingCard';

// Slugify a registry category name so DOM ids and sidebar hrefs match. Keep
// the same shape (lowercase, hyphenated) everywhere the sidebar mounts and
// where the Display card renders its per-category sections.
const categorySlug = (cat) => `display-${cat.toLowerCase().replace(/\s+/g, '-')}`;

/**
 * Every timezone the browser knows, so this works for an MSP anywhere rather
 * than a hand-kept list of US zones. Falls back to a short list on anything
 * old enough not to support it.
 */
const TIMEZONES = (() => {
  try {
    const all = Intl.supportedValuesOf('timeZone');
    if (all?.length) return all;
  } catch {
    // older runtime
  }
  return [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Australia/Sydney',
    'UTC',
  ];
})();

/**
 * Currencies the browser can format, for the currency picker. The server
 * validates against its own list on save, so a code the browser knows and
 * the server does not is refused there rather than stored.
 */
const CURRENCIES = (() => {
  try {
    const all = Intl.supportedValuesOf('currency');
    if (all?.length) return all;
  } catch {
    // older runtime
  }
  return ['USD', 'CAD', 'GBP', 'EUR', 'AUD', 'NZD'];
})();

// A starting point, not a limit: the field accepts any BCP-47 tag the server can format in.
const COMMON_LOCALES = ['en-US', 'en-CA', 'en-GB', 'en-AU', 'en-NZ', 'en-IE', 'fr-CA', 'fr-FR', 'de-DE', 'es-ES', 'es-MX', 'nl-NL'];

/** What the browser would write for a given locale and currency, so the admin sees the effect before saving. */
function previewFormat(locale, currency) {
  try {
    const money = new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD' }).format(1234.5);
    const date = new Date(2026, 9, 15).toLocaleDateString(locale || 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    return `${money} · ${date}`;
  } catch {
    return 'Not a locale or currency this browser can format.';
  }
}

export default function PortalSettingsPage() {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState({
    companyName: '',
    supportEmail: '',
    supportPhone: '',
    companyWebsite: '',
    companyAddress: '',
    companyLogoUrl: '',
    primaryColor: '#f97316',
    timezone: '',
    // The app shell (5c) and locale (5e); see config/branding.js on the backend.
    portalName: '',
    title: '',
    faviconUrl: '',
    loginLogoUrl: '',
    loginBackgroundUrl: '',
    poweredBy: true,
    locale: DEFAULT_LOCALE,
    currency: DEFAULT_CURRENCY,
    notifications: {
      quoteApproved: false,
      quoteRejected: false,
    },
    payments: {
      depositsEnabled: true,
      defaultDepositMethod: 'card',
      creditCardSurchargeEnabled: true,
      creditCardSurchargeType: 'percentage', // 'percentage' or 'fixed'
      creditCardSurchargeValue: 4, // 4% or $4.00 depending on type
    },
    email: {
      tenantDomain: '',
      fallbackFromAddress: '',
      fallbackFromName: '',
      fromName: '',
      fromAddress: '',
      replyTo: '',
    },
  });
  // Read-only: what the portal would send as right now (server-resolved).
  const [emailSender, setEmailSender] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [success, setSuccess] = useState(false);
  // Removing "Powered by" is the White label add-on (Phase 7a). Without it
  // the toggle is shown locked, and the backend refuses a false with 409.
  const whitelabel = useModuleEntitled('whitelabel');
  const paymentsEntitled = useModuleEntitled('payments');

  // Fetch current settings
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal-settings'],
    queryFn: () => portalSettingsAPI.get(),
  });

  // Update settings when data loads
  useEffect(() => {
    if (data?.data) {
      setSettings({
        companyName: data.data.companyName || '',
        supportEmail: data.data.supportEmail || '',
        supportPhone: data.data.supportPhone || '',
        companyWebsite: data.data.companyWebsite || '',
        companyAddress: data.data.companyAddress || '',
        companyLogoUrl: data.data.companyLogoUrl || '',
        primaryColor: data.data.primaryColor || '#f97316',
        timezone: data.data.timezone || '',
        portalName: data.data.portalName || '',
        title: data.data.title || '',
        faviconUrl: data.data.faviconUrl || '',
        loginLogoUrl: data.data.loginLogoUrl || '',
        loginBackgroundUrl: data.data.loginBackgroundUrl || '',
        poweredBy: data.data.poweredBy !== false,
        locale: data.data.locale || 'en-US',
        currency: data.data.currency || 'USD',
        notifications: {
          quoteApproved: data.data.notifications?.quoteApproved || false,
          quoteRejected: data.data.notifications?.quoteRejected || false,
        },
        payments: {
          depositsEnabled: data.data.payments?.depositsEnabled !== false,
          defaultDepositMethod: data.data.payments?.defaultDepositMethod || 'card',
          creditCardSurchargeEnabled: data.data.payments?.creditCardSurchargeEnabled !== false,
          creditCardSurchargeType: data.data.payments?.creditCardSurchargeType || 'percentage',
          creditCardSurchargeValue: data.data.payments?.creditCardSurchargeValue ?? 4,
        },
        email: {
          tenantDomain: data.data.email?.tenantDomain || '',
          fallbackFromAddress: data.data.email?.fallbackFromAddress || '',
          fallbackFromName: data.data.email?.fallbackFromName || '',
          fromName: data.data.email?.fromName || '',
          fromAddress: data.data.email?.fromAddress || '',
          replyTo: data.data.email?.replyTo || '',
        },
      });
      setEmailSender(data.data.emailSender || null);
      setHasChanges(false);
    }
  }, [data]);

  // Fetch CW email connectors so the admin can see which boards have
  // closed-loop replies configured.
  const { data: connectorsData } = useQuery({
    queryKey: ['admin-email-connectors'],
    queryFn: () => adminTicketsAPI.listEmailConnectors(),
  });
  const boardConnectors = connectorsData?.data?.items || [];

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (newSettings) => portalSettingsAPI.update(newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-settings'] });
      setHasChanges(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    },
  });

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleNotificationToggle = (key) => {
    setSettings((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key],
      },
    }));
    setHasChanges(true);
  };

  const handlePaymentChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      payments: {
        ...prev.payments,
        [key]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleEmailChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      email: {
        ...prev.email,
        [key]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(settings);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  const sidebarSections = [
    { id: 'company', label: 'Company Information' },
    { id: 'contact', label: 'Support Contact' },
    { id: 'branding', label: 'Branding' },
    { id: 'notifications', label: 'Email Notifications' },
    { id: 'payments', label: 'Payment Settings' },
    ...(paymentsEntitled ? [{ id: 'autopay', label: 'AutoPay Charging' }] : []),
    {
      id: 'display',
      label: 'Display Settings',
      subsections: REGISTRY_CATEGORIES.map(cat => ({
        id: `display-${cat.toLowerCase().replace(/\s+/g, '-')}`,
        label: cat,
      })),
    },
    { id: 'ticket-email', label: 'Ticket Email' },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Settings className="w-6 h-6 text-neutral-400" />
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Portal Settings</h1>
        </div>
        <p className="text-neutral-500 dark:text-neutral-400">
          Configure your company branding and contact information. These settings are used in emails and throughout the portal.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>Failed to load settings: {error.message}</span>
        </div>
      )}

      <div className="flex gap-8">
        <SettingsSidebar sections={sidebarSections} />

        <div className="flex-1 min-w-0 space-y-6">

      {/* Company Information */}
      <div id="company" className="scroll-mt-20 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-neutral-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Company Information
            </h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Basic information about your company displayed in emails and the portal.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Company Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              <Building2 className="w-4 h-4" />
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={settings.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              placeholder="Your Company Name"
              className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Used in email subjects, headers, and footers.
            </p>
          </div>

          {/* Company Website */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              <Globe className="w-4 h-4" />
              Company Website
            </label>
            <input
              type="url"
              value={settings.companyWebsite}
              onChange={(e) => handleChange('companyWebsite', e.target.value)}
              placeholder="https://www.yourcompany.com"
              className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Timezone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              <Clock className="w-4 h-4" />
              Timezone
            </label>
            <select
              value={settings.timezone}
              onChange={(e) => handleChange('timezone', e.target.value)}
              className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Every date the portal shows or records falls back to this when nothing more specific
              applies. A time entry uses the technician's own ConnectWise timezone, and an invoice or
              quote date uses the customer company's — this is the answer when neither is known.
              {data?.data?.timezoneSource === 'fallback' && (
                <span className="mt-1 block font-medium text-amber-600 dark:text-amber-400">
                  Not configured yet, so dates are defaulting to America/Los_Angeles. Set it.
                </span>
              )}
              {data?.data?.timezoneSource === 'environment' && (
                <span className="mt-1 block text-neutral-500 dark:text-neutral-400">
                  Currently coming from PORTAL_DEFAULT_TIMEZONE. Saving here overrides it.
                </span>
              )}
            </p>
          </div>

          {/* Locale and currency */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                <Globe className="w-4 h-4" />
                Locale
              </label>
              <input
                type="text"
                list="portal-locales"
                value={settings.locale}
                onChange={(e) => handleChange('locale', e.target.value)}
                placeholder="en-US"
                className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              />
              <datalist id="portal-locales">
                {COMMON_LOCALES.map((l) => <option key={l} value={l} />)}
              </datalist>
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                A BCP-47 tag. Decides the order of a date and the thousands and decimal separators, in the portal and in every email.
              </p>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                <DollarSign className="w-4 h-4" />
                Currency
              </label>
              <select
                value={settings.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                Every amount the portal shows is written in this currency. It does not convert anything: the figures come from ConnectWise as they are.
              </p>
            </div>
            <p className="md:col-span-2 text-xs text-neutral-600 dark:text-neutral-300">
              Preview: <span className="font-mono">{previewFormat(settings.locale, settings.currency)}</span>
            </p>
          </div>

          {/* Company Address */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              <MapPin className="w-4 h-4" />
              Company Address
            </label>
            <textarea
              value={settings.companyAddress}
              onChange={(e) => handleChange('companyAddress', e.target.value)}
              placeholder="123 Main St, Suite 100&#10;City, State 12345"
              rows={2}
              className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Support Contact Information */}
      <div id="contact" className="scroll-mt-20 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-neutral-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Support Contact Information
            </h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Contact information displayed in welcome emails and support communications.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Support Email */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              <Mail className="w-4 h-4" />
              Support Email
            </label>
            <input
              type="email"
              value={settings.supportEmail}
              onChange={(e) => handleChange('supportEmail', e.target.value)}
              placeholder="support@yourcompany.com"
              className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Displayed in emails as the support contact address.
            </p>
          </div>

          {/* Support Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              <Phone className="w-4 h-4" />
              Support Phone
            </label>
            <input
              type="tel"
              value={settings.supportPhone}
              onChange={(e) => handleChange('supportPhone', e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Displayed in emails as the support phone number.
            </p>
          </div>
        </div>
      </div>

      {/* Branding */}
      <div id="branding" className="scroll-mt-20 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-neutral-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Branding
            </h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Customize the look and feel of your portal and emails.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Primary Color */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              <Palette className="w-4 h-4" />
              Primary Color
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={settings.primaryColor}
                onChange={(e) => handleChange('primaryColor', e.target.value)}
                className="w-12 h-10 rounded border border-neutral-300 dark:border-neutral-600 cursor-pointer"
              />
              <input
                type="text"
                value={settings.primaryColor}
                onChange={(e) => handleChange('primaryColor', e.target.value)}
                placeholder="#f97316"
                className="w-32 px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              />
              <div
                className="px-4 py-2 rounded-lg text-white font-medium"
                style={{ backgroundColor: settings.primaryColor }}
              >
                Preview Button
              </div>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Used for buttons and accents in emails.
            </p>
          </div>

          {/* Logo URL */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              <Image className="w-4 h-4" />
              Logo URL
            </label>
            <input
              type="url"
              value={settings.companyLogoUrl}
              onChange={(e) => handleChange('companyLogoUrl', e.target.value)}
              placeholder="https://yourcompany.com/logo.png"
              className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              URL to your company logo (recommended: PNG or SVG, max width 200px).
            </p>
            {settings.companyLogoUrl && (
              <div className="mt-3 p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">Preview:</p>
                <img
                  src={settings.companyLogoUrl}
                  alt="Company Logo Preview"
                  className="max-h-16 max-w-[200px] object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <p className="text-sm text-red-500 hidden">Failed to load image</p>
              </div>
            )}
          </div>

          {/* App shell: what the browser and the login page show */}
          <div className="pt-6 border-t border-neutral-200 dark:border-neutral-700 space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Portal Shell</h3>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                What visitors see before they log in: the browser tab, the favicon and the login page. Resolved from the
                portal&apos;s address, so a customer on your domain never sees anyone else&apos;s name.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  <Building2 className="w-4 h-4" />
                  Portal Name
                </label>
                <input
                  type="text"
                  value={settings.portalName}
                  onChange={(e) => handleChange('portalName', e.target.value)}
                  placeholder={settings.companyName || 'Defaults to the company name'}
                  maxLength={100}
                  className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  Shown in the sidebar, on the login page and as <code>{'{{portalName}}'}</code> in emails. Leave blank to use the company name.
                </p>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  <Settings className="w-4 h-4" />
                  Browser Title
                </label>
                <input
                  type="text"
                  value={settings.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder={`${settings.portalName || settings.companyName || 'Portal'} Customer Portal`}
                  maxLength={150}
                  className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">The browser tab. Leave blank for &quot;&lt;Portal Name&gt; Customer Portal&quot;.</p>
              </div>
            </div>

            {[
              { key: 'faviconUrl', label: 'Favicon URL', hint: 'A square PNG or SVG, 32px or larger. https:// only.' },
              { key: 'loginLogoUrl', label: 'Login Page Logo URL', hint: 'Shown on the dark left panel of the login page, so a light-on-transparent logo reads best. Leave blank to use the Logo URL above.' },
              { key: 'loginBackgroundUrl', label: 'Login Page Background URL', hint: 'A photo behind the login panel; it is darkened so the text stays readable. Leave blank for the plain gradient.' },
            ].map((field) => (
              <div key={field.key}>
                <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  <Image className="w-4 h-4" />
                  {field.label}
                </label>
                <input
                  type="url"
                  value={settings[field.key]}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  placeholder="https://"
                  className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{field.hint}</p>
              </div>
            ))}

            <div
              className={`flex items-center justify-between py-3 px-4 rounded-lg ${whitelabel ? 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer' : 'cursor-not-allowed opacity-75'}`}
              onClick={() => whitelabel && handleChange('poweredBy', !settings.poweredBy)}
            >
              <div>
                <p className="font-medium text-neutral-900 dark:text-white">Show &quot;Powered by HaloMTS&quot;</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  A small line at the foot of the sidebar and the login page. Turn it off to white-label the portal completely.
                </p>
                {!whitelabel && <NotInPlanNotice className="mt-1" />}
              </div>
              <button
                type="button"
                disabled={!whitelabel}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed ${
                  settings.poweredBy ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-600'
                }`}
                role="switch"
                aria-checked={settings.poweredBy}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    settings.poweredBy ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email Notifications */}
      <div id="notifications" className="scroll-mt-20 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-neutral-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Email Notifications
            </h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Control which email notifications are sent from the portal. Disable notifications that are already handled by ConnectWise to avoid duplicates.
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-1">
            {/* System emails note */}
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>System emails</strong> (Welcome, Email Verification, Password Reset) are always enabled as they are essential for portal functionality.
              </p>
            </div>

            {/* Notification toggles */}
            {[
              { key: 'quoteApproved', label: 'Quote Approved', description: 'Send email when a customer approves/signs a quote' },
              { key: 'quoteRejected', label: 'Quote Rejected', description: 'Send email when a customer declines a quote' },
            ].map((notification) => (
              <div
                key={notification.key}
                className="flex items-center justify-between py-3 px-4 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 rounded-lg cursor-pointer"
                onClick={() => handleNotificationToggle(notification.key)}
              >
                <div>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {notification.label}
                  </p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    {notification.description}
                  </p>
                </div>
                <button
                  type="button"
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    settings.notifications[notification.key]
                      ? 'bg-primary-600'
                      : 'bg-neutral-200 dark:bg-neutral-600'
                  }`}
                  role="switch"
                  aria-checked={settings.notifications[notification.key]}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.notifications[notification.key] ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Payment Settings */}
      <div id="payments" className="scroll-mt-20 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-neutral-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Credit Card Surcharge (Global Defaults)
            </h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Configure default credit card surcharges. Companies can override these in their settings.
            Payment method availability is controlled via Feature Flags.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Deposit Payments */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div>
                <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Quote Deposit Payments
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Allow customers to pay deposits on accepted quotes. Companies can override this setting individually.
                </p>
              </div>
              <button
                type="button"
                onClick={() => handlePaymentChange('depositsEnabled', !settings.payments.depositsEnabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  settings.payments.depositsEnabled
                    ? 'bg-primary-600'
                    : 'bg-neutral-200 dark:bg-neutral-600'
                }`}
                role="switch"
                aria-checked={settings.payments.depositsEnabled}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    settings.payments.depositsEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Default Deposit Payment Method */}
          {settings.payments.depositsEnabled && (
            <div>
              <div className="mb-3">
                <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Default Deposit Payment Method
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Which payment method is shown first when a customer opens the deposit screen.
                </p>
              </div>
              <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden w-fit">
                <button
                  type="button"
                  onClick={() => handlePaymentChange('defaultDepositMethod', 'card')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    settings.payments.defaultDepositMethod === 'card'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                  }`}
                >
                  Credit Card
                </button>
                <button
                  type="button"
                  onClick={() => handlePaymentChange('defaultDepositMethod', 'ach')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    settings.payments.defaultDepositMethod === 'ach'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                  }`}
                >
                  ACH / Bank Account
                </button>
              </div>
            </div>
          )}

          <div className="border-t border-neutral-200 dark:border-neutral-700" />

          {/* Credit Card Surcharge */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Credit Card Surcharge
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Add a processing fee for credit card payments
                </p>
              </div>
              <button
                type="button"
                onClick={() => handlePaymentChange('creditCardSurchargeEnabled', !settings.payments.creditCardSurchargeEnabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  settings.payments.creditCardSurchargeEnabled
                    ? 'bg-primary-600'
                    : 'bg-neutral-200 dark:bg-neutral-600'
                }`}
                role="switch"
                aria-checked={settings.payments.creditCardSurchargeEnabled}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    settings.payments.creditCardSurchargeEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {settings.payments.creditCardSurchargeEnabled && (
              <div className="space-y-4 p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                {/* Surcharge Type */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Surcharge Type
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => handlePaymentChange('creditCardSurchargeType', 'percentage')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        settings.payments.creditCardSurchargeType === 'percentage'
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                      }`}
                    >
                      <Percent className="w-4 h-4" />
                      Percentage
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePaymentChange('creditCardSurchargeType', 'fixed')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        settings.payments.creditCardSurchargeType === 'fixed'
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                      }`}
                    >
                      <DollarSign className="w-4 h-4" />
                      Fixed Amount
                    </button>
                  </div>
                </div>

                {/* Surcharge Value */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    {settings.payments.creditCardSurchargeType === 'percentage' ? 'Surcharge Percentage' : 'Surcharge Amount'}
                  </label>
                  <div className="relative max-w-xs">
                    {settings.payments.creditCardSurchargeType === 'fixed' && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
                    )}
                    <input
                      type="number"
                      step={settings.payments.creditCardSurchargeType === 'percentage' ? '0.1' : '0.01'}
                      min="0"
                      max={settings.payments.creditCardSurchargeType === 'percentage' ? '100' : undefined}
                      value={settings.payments.creditCardSurchargeValue}
                      onChange={(e) => handlePaymentChange('creditCardSurchargeValue', parseFloat(e.target.value) || 0)}
                      className={`w-full py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        settings.payments.creditCardSurchargeType === 'fixed' ? 'pl-8 pr-4' : 'px-4'
                      }`}
                    />
                    {settings.payments.creditCardSurchargeType === 'percentage' && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500">%</span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                    {settings.payments.creditCardSurchargeType === 'percentage'
                      ? `Example: A $100 payment would have a ${settings.payments.creditCardSurchargeValue}% surcharge of ${formatMoney((100 * settings.payments.creditCardSurchargeValue / 100))}`
                      : `Example: A $100 payment would have a fixed surcharge of ${formatMoney(settings.payments.creditCardSurchargeValue)}`
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AutoPay charging switch (Phase 7e) — saved on its own, audited */}
      {paymentsEntitled && <AutopayChargingCard />}

      {/* Display Settings — customer-screen label/visibility overrides */}
      <GlobalDisplaySettingsCard />

      {/* Ticket Email (closed-loop replies via CW connector) */}
      <div id="ticket-email" className="scroll-mt-20 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-neutral-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Ticket Email (Closed-Loop Replies)
            </h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            When an admin emails a note from the portal, we send via ConnectWise's email connector
            address for the ticket's board so replies route back into CW and update the ticket.
            The subject format is <code>Ticket#NNNN/...</code> — CW's standard parser pattern.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Sender identity (Phase 5b) */}
          <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Portal sender</h3>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Who invitations, password resets, quote and invoice emails are from. Blank fields use the
                Email delivery provider's values (Administration → Integrations). The address must be on the
                sender domain configured there, and that domain must have verified before anything is sent.
              </p>
            </div>
            {emailSender && (
              <div className={`flex items-start gap-2 rounded-lg p-3 text-sm ${emailSender.ready ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200' : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'}`}>
                {emailSender.ready ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                <div>
                  {emailSender.ready ? (
                    <>
                      Sending as <strong>{emailSender.fromName ? `${emailSender.fromName} <${emailSender.fromAddress}>` : emailSender.fromAddress}</strong>
                      {emailSender.replyTo && <> (replies to {emailSender.replyTo})</>}
                      {' '}via {emailSender.mode === 'smtp' ? 'your own SMTP server' : emailSender.mode === 'smtp2go' ? 'SMTP2GO' : 'dev mode — nothing is sent'}
                      {emailSender.source === 'env' && <>, from the server environment</>}.
                    </>
                  ) : (
                    <>Email cannot be sent right now: {emailSender.reason}</>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">From Name</label>
                <input
                  type="text"
                  value={settings.email.fromName}
                  onChange={(e) => handleEmailChange('fromName', e.target.value)}
                  placeholder="Acme Support"
                  maxLength={100}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">From Address</label>
                <input
                  type="email"
                  value={settings.email.fromAddress}
                  onChange={(e) => handleEmailChange('fromAddress', e.target.value)}
                  placeholder="portal@example.com"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Reply-To</label>
                <input
                  type="email"
                  value={settings.email.replyTo}
                  onChange={(e) => handleEmailChange('replyTo', e.target.value)}
                  placeholder="support@example.com"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800"
                />
              </div>
            </div>
          </div>

          {/* Tenant domain */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Tenant Domain
            </label>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
              Used in the email threading headers (<code>In-Reply-To</code>, <code>References</code>).
              Most email clients also use this to group replies. Blank means the verified sender domain, then the From address's domain.
            </p>
            <input
              type="text"
              value={settings.email.tenantDomain}
              onChange={(e) => handleEmailChange('tenantDomain', e.target.value)}
              placeholder="example.com"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800"
            />
          </div>

          {/* Fallback From */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Fallback From Address
              </label>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                Used when a board has no email connector. Otherwise the board's <code>notifyEmailFrom</code> wins.
              </p>
              <input
                type="email"
                value={settings.email.fallbackFromAddress}
                onChange={(e) => handleEmailChange('fallbackFromAddress', e.target.value)}
                placeholder="support@example.com"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Fallback From Name
              </label>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                Display name paired with the fallback address.
              </p>
              <input
                type="text"
                value={settings.email.fallbackFromName}
                onChange={(e) => handleEmailChange('fallbackFromName', e.target.value)}
                placeholder="Acme Support"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800"
              />
            </div>
          </div>

          {/* Per-board connector detection (read-only diagnostic table) */}
          <div>
            <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Detected ConnectWise Email Connectors
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
              Per board, what CW will use as the From address and which mailbox CW polls for replies.
              Edit these in ConnectWise: <em>System Tables → Service Boards → [board] → Notification fields</em>{' '}
              and <em>System Setup → Setup Tables → Email Connector</em>.
            </p>
            <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 dark:bg-neutral-700/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-neutral-700 dark:text-neutral-300">Board</th>
                    <th className="text-left px-3 py-2 font-medium text-neutral-700 dark:text-neutral-300">From (notifyEmailFrom)</th>
                    <th className="text-left px-3 py-2 font-medium text-neutral-700 dark:text-neutral-300">Connector Mailbox (polled)</th>
                    <th className="text-left px-3 py-2 font-medium text-neutral-700 dark:text-neutral-300">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                  {boardConnectors.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-4 text-center text-neutral-500 dark:text-neutral-400">
                        Loading…
                      </td>
                    </tr>
                  ) : (
                    boardConnectors.map((b) => {
                      const hasFrom = !!b.notifyEmailFrom;
                      const hasMailbox = !!b.connectorMailbox;
                      const ok = hasFrom && hasMailbox;
                      return (
                        <tr key={b.boardId} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/30">
                          <td className="px-3 py-2 font-medium text-neutral-900 dark:text-white">{b.boardName}</td>
                          <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">
                            {hasFrom ? (
                              <>
                                {b.notifyEmailFromName && (
                                  <span className="text-neutral-500 dark:text-neutral-400">{b.notifyEmailFromName} </span>
                                )}
                                <code className="text-xs">{b.notifyEmailFrom}</code>
                              </>
                            ) : (
                              <span className="text-yellow-600 dark:text-yellow-400">— not set —</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">
                            {hasMailbox ? (
                              <code className="text-xs">{b.connectorMailbox}</code>
                            ) : (
                              <span className="text-neutral-400">none</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {ok ? (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded">
                                <CheckCircle className="w-3 h-3" /> Closed-loop ready
                              </span>
                            ) : hasFrom ? (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 rounded">
                                <AlertCircle className="w-3 h-3" /> Replies won't parse
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded">
                                Falls back to settings
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>


      {/* Save Button */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {success && (
              <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                Settings saved successfully
              </span>
            )}
            {saveMutation.isError && (
              <span className="flex items-center gap-1 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                Failed to save: {saveMutation.error?.response?.data?.error || saveMutation.error?.message}
              </span>
            )}
          </div>

          <button
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isPending || !settings.companyName.trim()}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Settings
          </button>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Standalone Display Settings card with its own fetch + save flow so it
 * doesn't have to coexist with PortalSettingsPage's monolithic settings blob.
 * Also invalidates the customer-side `displaySettings` query on save so any
 * open customer pages pick up the change on next render.
 */
function GlobalDisplaySettingsCard() {
  const queryClient = useQueryClient();
  const [overrides, setOverrides] = useState({});
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['portal-display-overrides'],
    queryFn: () => displaySettingsAPI.adminGetGlobal().then(res => res.data.overrides || {}),
  });

  useEffect(() => {
    if (data) {
      setOverrides(data);
      setDirty(false);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (next) => displaySettingsAPI.adminUpdateGlobal(next),
    onSuccess: () => {
      setDirty(false);
      setSavedAt(Date.now());
      queryClient.invalidateQueries({ queryKey: ['portal-display-overrides'] });
      queryClient.invalidateQueries({ queryKey: ['displaySettings'] });
    },
  });

  const handleChange = (next) => {
    setOverrides(next);
    setDirty(true);
  };

  return (
    <div id="display" className="scroll-mt-20 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary-500" />
            Display Settings
          </h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Customize which fields customers see on their portal screens and what labels they're shown under.
            Per-company overrides can be set on the company detail page.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {savedAt > 0 && !dirty && !saveMutation.isPending && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <CheckCircle className="w-3.5 h-3.5" /> Saved
            </span>
          )}
          {saveMutation.isError && (
            <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
              <AlertCircle className="w-3.5 h-3.5" /> {saveMutation.error?.response?.data?.error || 'Save failed'}
            </span>
          )}
          <button
            type="button"
            onClick={() => saveMutation.mutate(overrides)}
            disabled={!dirty || saveMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Display Settings
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-6 flex items-center gap-2 text-neutral-500 dark:text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading display settings…
        </div>
      ) : (
        <div className="space-y-8">
          {REGISTRY_CATEGORIES.map(category => (
            <section key={category} id={categorySlug(category)} className="scroll-mt-20">
              <h4 className="text-base font-semibold text-neutral-900 dark:text-white mb-3">{category}</h4>
              <DisplayOverridesEditor
                category={category}
                overrides={overrides}
                onChange={handleChange}
                inheritedOverrides={{}}
              />
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
